import { UserService } from '../../api/services/UserService';
import type { Routemize_DTOs_CreateUserWorkingHourOverrideDTO } from '../../api/models/Routemize_DTOs_CreateUserWorkingHourOverrideDTO';
import type { Routemize_DTOs_UpdateUserWorkingHourOverrideDTO } from '../../api/models/Routemize_DTOs_UpdateUserWorkingHourOverrideDTO';
import { Routemize_Constants_WorkingHourStatus } from '../../api/models/Routemize_Constants_WorkingHourStatus';

// Window names for identifying override types
export const OVERRIDE_WINDOW_NAMES = {
  DAY_OFF: 'Day Off',
  LUNCH: 'Lunch',
} as const;

// State returned when loading overrides for a specific date
export interface DayOverrideState {
  dayOff: {
    id: string;
    enabled: boolean;
  } | null;
  workDayOverride: {
    id: string;
    startTime: string;
    endTime: string;
  } | null;
  lunchBreak: {
    id: string;
    enabled: boolean;
    startTime: string;
    endTime: string;
  } | null;
}

/**
 * Manager class for handling user availability overrides (day off, lunch breaks)
 *
 * This manager provides CRUD operations for UserWorkingHourOverride records
 * which are used to override the default work schedule for specific dates.
 */
export class AvailabilityOverrideManager {
  // Retained for call-site compatibility (BookingsPage / Routy pass the company
  // timezone). Date keys below intentionally do NOT reproject through it — see
  // formatDateOnly.
  private companyTimezone: string | undefined;

  constructor(companyTimezone?: string) {
    this.companyTimezone = companyTimezone;
  }

  /**
   * Format a Date to DateOnly string format (YYYY-MM-DD) using the Date's
   * wall-clock calendar day (NOT a timezone reprojection).
   *
   * A per-date override's `date` field is a backend `DateOnly` — a pure
   * calendar-day label with no instant, serialized as "YYYY-MM-DD". The two
   * places that WRITE these keys — the desktop Availability editor
   * (`CalendarPage.toLocalDateString`) and the mobile editor
   * (`CalendarScreen` / `toLocalDateKey`) — both key by the clicked cell's
   * wall-clock day (`getFullYear/Month/Date`). This reader must use the same
   * frame so a date turned Off in the editor is found by the Bookings day view.
   *
   * Reprojecting the probe `Date` (browser-local midnight of the selected day)
   * through the tenant/company timezone drifts to the adjacent calendar day
   * whenever the browser is east of the tenant tz (or the tenant tz falls back
   * to UTC), which is exactly why disabled days still looked "wide open" in the
   * Bookings view (ClickUp 86bc5759x). Weekday/company-schedule resolution was
   * moved off tenant-tz for the same reason (see CalendarPage commit fbaf8329).
   */
  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get all overrides for a user and filter/organize by a specific date
   */
  async getOverridesForDate(userId: string, date: Date): Promise<DayOverrideState> {
    const dateString = this.formatDateOnly(date);

    // Fetch all overrides for the user
    const allOverrides = await UserService.getApiAppUserGetUserWorkHourOverrideAysnc(userId);

    // Filter for the specific date
    const dateOverrides = allOverrides.filter(
      (override) => override.date === dateString
    );

    // Find day off override (UNAVAILABLE, full day)
    const dayOffOverride = dateOverrides.find(
      (override) => override.windowName === OVERRIDE_WINDOW_NAMES.DAY_OFF
    );

    // Find work day override (AVAILABLE — user overriding a company day off to be available)
    const workDayOverride = dateOverrides.find(
      (override) =>
        override.windowName === 'Work Hours' &&
        override.status === Routemize_Constants_WorkingHourStatus.AVAILABLE
    );

    // Find lunch override
    const lunchOverride = dateOverrides.find(
      (override) => override.windowName === OVERRIDE_WINDOW_NAMES.LUNCH
    );

    // For lunch breaks:
    // - UNAVAILABLE status means lunch is ON (user wants a break, time is blocked)
    // - AVAILABLE status means lunch is OFF (user is working through lunch, time is available)
    // - No override means fall back to company default (which typically has lunch configured)
    const lunchEnabled = lunchOverride
      ? lunchOverride.status === Routemize_Constants_WorkingHourStatus.UNAVAILABLE
      : false; // No override = no explicit lunch break set for this day

    return {
      dayOff: dayOffOverride ? {
        id: dayOffOverride.id!,
        enabled: dayOffOverride.isEnabled ?? true,
      } : null,
      workDayOverride: workDayOverride ? {
        id: workDayOverride.id!,
        startTime: workDayOverride.startTime ?? '09:00',
        endTime: workDayOverride.endTime ?? '17:00',
      } : null,
      lunchBreak: lunchOverride ? {
        id: lunchOverride.id!,
        enabled: lunchEnabled,
        startTime: lunchOverride.startTime ?? '12:00',
        endTime: lunchOverride.endTime ?? '13:00',
      } : null,
    };
  }

  /**
   * Set or update the day off status for a specific date
   *
   * When enabled=true, creates an UNAVAILABLE override covering the entire day
   * When enabled=false, either disables or deletes the existing override
   */
  async setDayOff(
    userId: string,
    date: Date,
    enabled: boolean,
    existingOverrideId?: string
  ): Promise<void> {
    const dateString = this.formatDateOnly(date);

    if (enabled) {
      if (existingOverrideId) {
        // Update existing override to enabled
        const updateDto: Routemize_DTOs_UpdateUserWorkingHourOverrideDTO = {
          id: existingOverrideId,
          userId,
          date: dateString,
          startTime: '00:00',
          endTime: '23:59',
          status: Routemize_Constants_WorkingHourStatus.UNAVAILABLE,
          windowIndex: 0,
          windowName: OVERRIDE_WINDOW_NAMES.DAY_OFF,
          isEnabled: true,
        };
        await UserService.putApiAppUserUpdateUserWorkHourOverrideAysnc([updateDto]);
      } else {
        // Create new override
        const createDto: Routemize_DTOs_CreateUserWorkingHourOverrideDTO = {
          userId,
          date: dateString,
          startTime: '00:00',
          endTime: '23:59',
          status: Routemize_Constants_WorkingHourStatus.UNAVAILABLE,
          windowIndex: 0,
          windowName: OVERRIDE_WINDOW_NAMES.DAY_OFF,
          isEnabled: true,
        };
        await UserService.postApiAppUserCreateUserWorkHourOverrideAysnc([createDto]);
      }
    } else {
      if (existingOverrideId) {
        // Delete the override when disabling
        await UserService.deleteApiAppUserDeleteUserWorkHoursOverride(existingOverrideId);
      }
      // If no existing override and disabling, nothing to do
    }
  }

  /**
   * Set or update the lunch break status for a specific date
   *
   * When enabled=true, creates an UNAVAILABLE override for the lunch period
   * When enabled=false, either disables or deletes the existing override
   */
  async setLunchBreak(
    userId: string,
    date: Date,
    enabled: boolean,
    startTime: string,
    endTime: string,
    existingOverrideId?: string
  ): Promise<void> {
    const dateString = this.formatDateOnly(date);

    if (enabled) {
      if (existingOverrideId) {
        // Update existing override
        const updateDto: Routemize_DTOs_UpdateUserWorkingHourOverrideDTO = {
          id: existingOverrideId,
          userId,
          date: dateString,
          startTime,
          endTime,
          status: Routemize_Constants_WorkingHourStatus.UNAVAILABLE,
          windowIndex: 1,
          windowName: OVERRIDE_WINDOW_NAMES.LUNCH,
          isEnabled: true,
        };
        await UserService.putApiAppUserUpdateUserWorkHourOverrideAysnc([updateDto]);
      } else {
        // Create new override
        const createDto: Routemize_DTOs_CreateUserWorkingHourOverrideDTO = {
          userId,
          date: dateString,
          startTime,
          endTime,
          status: Routemize_Constants_WorkingHourStatus.UNAVAILABLE,
          windowIndex: 1,
          windowName: OVERRIDE_WINDOW_NAMES.LUNCH,
          isEnabled: true,
        };
        await UserService.postApiAppUserCreateUserWorkHourOverrideAysnc([createDto]);
      }
    } else {
      // When disabling lunch break, create an AVAILABLE override to explicitly mark
      // the lunch time as available for appointments (overriding company default)
      if (existingOverrideId) {
        // Update existing override to AVAILABLE
        const updateDto: Routemize_DTOs_UpdateUserWorkingHourOverrideDTO = {
          id: existingOverrideId,
          userId,
          date: dateString,
          startTime,
          endTime,
          status: Routemize_Constants_WorkingHourStatus.AVAILABLE,
          windowIndex: 1,
          windowName: OVERRIDE_WINDOW_NAMES.LUNCH,
          isEnabled: true,
        };
        await UserService.putApiAppUserUpdateUserWorkHourOverrideAysnc([updateDto]);
      } else {
        // Create new AVAILABLE override to skip lunch
        const createDto: Routemize_DTOs_CreateUserWorkingHourOverrideDTO = {
          userId,
          date: dateString,
          startTime,
          endTime,
          status: Routemize_Constants_WorkingHourStatus.AVAILABLE,
          windowIndex: 1,
          windowName: OVERRIDE_WINDOW_NAMES.LUNCH,
          isEnabled: true,
        };
        await UserService.postApiAppUserCreateUserWorkHourOverrideAysnc([createDto]);
      }
    }
  }

  /**
   * Override a company day-off to make the user available (or remove that override).
   *
   * When makeAvailable=true, creates an AVAILABLE override with the given work hours.
   * When makeAvailable=false, removes the override so the company default (day off) is restored.
   */
  async setWorkDayAvailable(
    userId: string,
    date: Date,
    makeAvailable: boolean,
    workHours: { startTime: string; endTime: string },
    existingOverrideId?: string
  ): Promise<void> {
    const dateString = this.formatDateOnly(date);

    if (makeAvailable) {
      if (existingOverrideId) {
        const updateDto: Routemize_DTOs_UpdateUserWorkingHourOverrideDTO = {
          id: existingOverrideId,
          userId,
          date: dateString,
          startTime: workHours.startTime,
          endTime: workHours.endTime,
          status: Routemize_Constants_WorkingHourStatus.AVAILABLE,
          windowIndex: 0,
          windowName: 'Work Hours',
          isEnabled: true,
        };
        await UserService.putApiAppUserUpdateUserWorkHourOverrideAysnc([updateDto]);
      } else {
        const createDto: Routemize_DTOs_CreateUserWorkingHourOverrideDTO = {
          userId,
          date: dateString,
          startTime: workHours.startTime,
          endTime: workHours.endTime,
          status: Routemize_Constants_WorkingHourStatus.AVAILABLE,
          windowIndex: 0,
          windowName: 'Work Hours',
          isEnabled: true,
        };
        await UserService.postApiAppUserCreateUserWorkHourOverrideAysnc([createDto]);
      }
    } else {
      if (existingOverrideId) {
        await UserService.deleteApiAppUserDeleteUserWorkHoursOverride(existingOverrideId);
      }
    }
  }

  /**
   * Delete an override by ID
   */
  async deleteOverride(id: string): Promise<void> {
    await UserService.deleteApiAppUserDeleteUserWorkHoursOverride(id);
  }
}

// Factory function — pass company timezone from context
export function createAvailabilityOverrideManager(companyTimezone?: string): AvailabilityOverrideManager {
  return new AvailabilityOverrideManager(companyTimezone);
}

/**
 * Default singleton. Per-date override keys are wall-clock calendar days (see
 * formatDateOnly), so the constructor timezone does not change lookups; the
 * mobile app's availability screens import this shared instance and stay
 * consistent with the mobile editor's wall-clock keys.
 */
export const availabilityOverrideManager = new AvailabilityOverrideManager();
